import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Form from "../../components/common/Form/Form";
import { setEducation, updateEducation, Education } from "../../store/slices/profileSlice";
import { RootState } from "../../store/store";
import "./Education.scss";
import { Typography } from "@mui/material";

interface EducationEntry {
    school: string;
    degree: string;
    fieldOfStudy: string;
    country: string;
    grade: string;
    currentlyStudyHere: boolean;
    startDate: string;
    endDate: string;
    description: string;
}

interface FormErrors {
    school: boolean;
    degree: boolean;
    startDate: boolean;
    endDate: boolean;
}

const initialFormData: EducationEntry = {
    school: "",
    degree: "",
    fieldOfStudy: "",
    country: "",
    grade: "",
    currentlyStudyHere: false,
    startDate: "",
    endDate: "",
    description: "",
};

const initialErrors: FormErrors = {
    school: false,
    degree: false,
    startDate: false,
    endDate: false,
};

const EducationPage: React.FC = () => {
    const dispatch = useDispatch();
    const education = useSelector((state: RootState) => state.profile.education);

    const [formData, setFormData] = useState<EducationEntry>(initialFormData);
    const [errors, setErrors] = useState<FormErrors>(initialErrors);
    const [editIndex, setEditIndex] = useState<number | null>(null);

    const handleInputChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
    ) => {
        const { name, value, type } = e.target;
        setFormData({
            ...formData,
            [name]: type === "checkbox" ? (e.target as HTMLInputElement).checked : value,
        });
        if (errors[name as keyof FormErrors]) {
            setErrors({
                ...errors,
                [name]: false,
            });
        }
    };

    const validateForm = (): boolean => {
        const newErrors: FormErrors = {
            school: !formData.school,
            degree: !formData.degree,
            startDate: !formData.startDate,
            endDate: !formData.endDate && !formData.currentlyStudyHere,
        };
        setErrors(newErrors);
        return !Object.values(newErrors).some(Boolean);
    };

    const handleSubmit = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        if (validateForm()) {
            const educationData: Education = {
                school: formData.school,
                degree: formData.degree,
                fieldOfStudy: formData.fieldOfStudy,
                grade: formData.grade,
                from: formData.startDate,
                to: formData.endDate,
                current: formData.currentlyStudyHere,
                description: formData.description,
            };

            if (editIndex !== null) {
                dispatch(updateEducation({ index: editIndex, education: educationData }));
                setEditIndex(null);
            } else {
                dispatch(setEducation(educationData));
            }
            setFormData(initialFormData);
        } else {
            // console.log("Form has errors");
        }
    };

    return (
        <div className="form-wrapper">
            <Form
                header={editIndex !== null ? "Edit Education" : "Add Education"}
                className="education-form"
            >
                {/* Add "* indicates required" note at the top */}
                <Typography
                    variant="caption"
                    sx={{
                        fontSize: "13px", // Matches the 79x13 size
                        fontWeight: 400,
                        color: "#666",
                        marginBottom: "15px",
                        display: "block",
                    }}
                >
                    * indicates required
                </Typography>

                <label className="form-label">School*</label>
                <input
                    className={`form-input ${errors.school ? "error" : ""}`}
                    type="text"
                    name="school"
                    placeholder="Ex: University of Toronto"
                    value={formData.school}
                    onChange={handleInputChange}
                />
                {errors.school && <span className="error-text">School is required</span>}

                <label className="form-label">Degree*</label>
                <input
                    className={`form-input ${errors.degree ? "error" : ""}`}
                    type="text"
                    name="degree"
                    placeholder="Ex: Bachelor's"
                    value={formData.degree}
                    onChange={handleInputChange}
                />
                {errors.degree && <span className="error-text">Degree is required</span>}

                <label className="form-label">Field of Study</label>
                <input
                    className="form-input"
                    type="text"
                    name="fieldOfStudy"
                    placeholder="Ex: Computer Science"
                    value={formData.fieldOfStudy}
                    onChange={handleInputChange}
                />

                <label className="form-label">Country</label>
                <input
                    className="form-input"
                    type="text"
                    name="country"
                    placeholder="Ex: Canada"
                    value={formData.country}
                    onChange={handleInputChange}
                />

                <label className="form-label">Grade</label>
                <input
                    className="form-input"
                    type="text"
                    name="grade"
                    placeholder="Ex: 3.8 GPA"
                    value={formData.grade}
                    onChange={handleInputChange}
                />

                <div className="form-checkbox">
                    <input
                        type="checkbox"
                        name="currentlyStudyHere"
                        checked={formData.currentlyStudyHere}
                        onChange={handleInputChange}
                    />
                    <label>I currently study here</label>
                </div>

                <label className="form-label">Start date*</label>
                <input
                    className={`form-input ${errors.startDate ? "error" : ""}`}
                    type="date"
                    name="startDate"
                    value={formData.startDate}
                    onChange={handleInputChange}
                />
                {errors.startDate && <span className="error-text">Start date is required</span>}

                <label className="form-label">End date*</label>
                <input
                    className={`form-input ${errors.endDate ? "error" : ""}`}
                    type="date"
                    name="endDate"
                    value={formData.endDate}
                    onChange={handleInputChange}
                    disabled={formData.currentlyStudyHere}
                />
                {errors.endDate && (
                    <span className="error-text">
                        End date is required unless currently studying
                    </span>
                )}

                <label className="form-label">Description</label>
                <textarea
                    className="form-input"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                />

                <button className="form-button" type="submit" onClick={handleSubmit}>
                    {editIndex !== null ? "Update Education" : "Save"}
                </button>
            </Form>
        </div>
    );
};

export default EducationPage;
